/**
 * @author: subhasish.ghosh@zoom.us
 */

import dayjs from "dayjs";
import {
  CANCELLATION_REASONS,
  DEFAULT_SORT_ORDER,
  NON_DECIMAL_CURRENCY_CODES,
  RESOLUTION_DATE_CASES,
  SIDEBAR_ITEMS,
  TABLE_TYPES_ENTRIES,
  TABLE_TYPES_KEYS,
  TNS_REASON_CODE_DESCRIPTIONS,
  UNCATEGORIZED,
} from "./constants";
import { DATE_FORMAT, DATE_FORMAT_EXPORT_FILE } from "./timeUnits";

import flatMap from "lodash/flatMap";

import { v4 as uuidv4 } from "uuid";

export const getEnvironment = () => process.env.DEPLOY_ENV;

export const isProdEnvironment = () =>
  ["prod", "production"].includes(process.env.DEPLOY_ENV);

export const isLocalEnvironment = () => "local" === process.env.DEPLOY_ENV;

export const getFormattedAmount = (amount) => {
  const number = amount || 0.0;
  return number.toLocaleString("en-US", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
};

export const getFormattedAmountNonDecimalCurrency = (amount) => {
  const number = amount || 0;
  return number.toLocaleString("en-US", {
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  });
};

export const isNonDecimalCurrency = (currency) =>
  NON_DECIMAL_CURRENCY_CODES.includes(currency);

export const getDataGridSortOrderForPayload = (sortOrder, tabType) => {
  // use default sort for tables when no sorting is applied on columns by user
  if (!sortOrder?.length) {
    let defaultOrder = {};
    if (tabType === TABLE_TYPES_KEYS[0]) {
      defaultOrder = DEFAULT_SORT_ORDER[TABLE_TYPES_KEYS[0]];
    } else if (tabType === TABLE_TYPES_KEYS[1]) {
      defaultOrder = DEFAULT_SORT_ORDER[TABLE_TYPES_KEYS[1]];
    }
    return { [defaultOrder.field]: defaultOrder.sort };
  }

  // only single sort is allowed by lib
  const curr = sortOrder?.[0];
  if (curr && curr.field && curr.sort) {
    return { [curr.field]: curr.sort };
  }
  return {};
};

export const isValidDate = (dateString) =>
    dateString && dateString !== null && dayjs(dateString).isValid();

export const formatDateToPST = (dateString) =>
  dayjs(dateString).tz("America/Los_Angeles").format(DATE_FORMAT);

export const formatDate = (dateString) => dayjs(dateString).format(DATE_FORMAT);

export const getToday = () => dayjs();

export const getOneYearAgo = () => dayjs().subtract(365, "days");

export const isSelectedActionTypeNonPaymentDirect = (selectedActionType) =>
  selectedActionType === "nonPaymentDirect";

export const isSelectedActionTypeNonPaymentIndirect = (selectedActionType) =>
  selectedActionType === "nonPaymentIndirect";

export const isSelectedActionTypeBizOperationDirect = (selectedActionType) =>
  selectedActionType === "bizOperations";

export const isSelectedActionTypeFraud = (selectedActionType) =>
  selectedActionType === "fraud";

export const isSelectedActionTypeTNS = (selectedActionType) =>
  selectedActionType === "tns";

/**
 * Resolves the hover-over description for a TNS cancellation reason (reasonCode).
 * Accepts either the display label ("Fraudulent Account", "Fraud - Unauthorized Reseller")
 * or the raw code ("tos_violation"). Returns "" when there is no mapping, so callers can
 * fall back to showing the raw value in the tooltip.
 */
export const getTnsReasonDescription = (reasonCode) => {
  if (!reasonCode || typeof reasonCode !== "string") return "";
  const key = reasonCode
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "_")
    .replace(/^_+|_+$/g, "");
  return TNS_REASON_CODE_DESCRIPTIONS[key] || "";
};

export const isSelectedActionTypeSummary = (selectedActionType) =>
  selectedActionType === "summary";

export const isSelectedActionTypeAdmin = (selectedActionType) =>
  selectedActionType === "admin";

export const enableWorkflowActionEndpoints = (selectedActionType) =>
  selectedActionType &&
  !isSelectedActionTypeSummary(selectedActionType) &&
  !isSelectedActionTypeAdmin(selectedActionType);

export const hasMissingResolutionDate = (resolutionDate) =>
  ["", null, undefined].includes(resolutionDate);

export const getCancellationReasonsForSelectedActionType = (
  selectedActionType
) => CANCELLATION_REASONS[selectedActionType] || {};

// A user can now hold multiple roles at once (e.g. "Readonly" left over alongside a real
// approver role like "Trust and Safety Senior Manager"). Checking the single back-compat
// `role` string is wrong here - it's arbitrarily one of the user's role documents, not
// necessarily "Readonly" even when they hold it, and not necessarily NOT "Readonly" when
// they hold something else too. Only treat the user as globally read-only if EVERY role
// they hold is "Readonly" - any other role means they have real capability somewhere.
export const isCurrentRoleReadonly = (roles) =>
  Array.isArray(roles) && roles.length > 0 && roles.every((role) => role === "Readonly");

/**
 * Returns the level number (1, 2, ...) of the first level on a workflow-action
 * row that hasn't been decided yet, or null if every level is finalized.
 * A level's `lNStatus` field is null on the row until someone approves/rejects it.
 */
export const getCurrentPendingLevel = (row) => {
  if (!row) return null;
  if (!row.l1Status) return 1;
  if (!row.l2Status) return 2;
  return null;
};

/**
 * Whether `userEmail` is one of the approvers assigned to a row's currently
 * pending level (row.levelApproves[<level>]). This is per-row, per-level data
 * returned by the backend, so it correctly reflects who can actually approve
 * *this* record right now - unlike the user's single resolved `role`, which
 * only tells you what they can approve in general (and, for a Defer Approver,
 * doesn't tell you anything at all, since defer-visibility is independent of
 * approve-eligibility).
 */
export const isUserEligibleToApproveRow = (row, userEmail) => {
  const pendingLevel = getCurrentPendingLevel(row);
  if (!pendingLevel || !userEmail) return false;
  const approvers = row?.levelApproves?.[pendingLevel] || [];
  return approvers.some((approver) => approver?.email === userEmail);
};

/**
 * Approve is only meaningful when every selected row is still pending AND the
 * current user is an assigned approver for each row's currently-pending
 * level. A single ineligible row (e.g. one where only L2 remains pending and
 * the user is only an L1 approver) disables the whole bulk action, since the
 * "Approve Cancellation(s)" button acts on the full selection at once.
 */
export const isApproveEligibleForSelection = (selectedRows, userEmail) =>
  Array.isArray(selectedRows) &&
  selectedRows.length > 0 &&
  selectedRows.every((row) => isUserEligibleToApproveRow(row, userEmail));

/**
 * A pure Renewals Defer Approver is never listed in a row's levelApproves map -
 * defer eligibility is an additive grant independent of the normal
 * assigned-approver mechanism (see WorkFlowUtils.java's Group C visibility rule
 * on the backend). So isUserEligibleToApproveRow will always be false for them,
 * and isRowSelectable must OR in this separate check or every row ends up
 * unselectable for a defer-only user. Mirrors the backend criteria: status must
 * still be "Hold for Cancel" and at least one level must still be pending.
 *
 * Deliberately checks `selectedActionType` (the active tab/query param, e.g.
 * "nonRenewalDirect") rather than the row's own `actionType` field - the row's
 * actionType is the raw backend value (e.g. "NON_AUTO_RENEWALS"), not the
 * frontend action-type key, so comparing row.actionType against
 * "nonRenewalDirect" would never match.
 */
export const isUserEligibleToDeferRow = (
  row,
  isRenewalsDeferApprover,
  selectedActionType
) =>
  Boolean(isRenewalsDeferApprover) &&
  selectedActionType === "nonRenewalDirect" &&
  row?.status === "Hold for Cancel" &&
  (!row?.l1Status || !row?.l2Status);

export const isCurrentRoleAdmin = (currentRole) => currentRole === "admin";

export const isApproverLevelOne = (approverLevel) =>
  approverLevel === "level_one";

export const getTop10AndRemoveUncategorisedIndustry = (data) =>
  data?.filter((item) => item?.id !== UNCATEGORIZED)?.slice(0, 10);

export const goToTop = () => window.scrollTo({ top: 0, behavior: "smooth" });

export const applyPromisedToPayFunc = (applyToAll) => {
  if (!applyToAll) {
    return RESOLUTION_DATE_CASES.apply_to_none;
  }

  return RESOLUTION_DATE_CASES[applyToAll];
};

export const getTableTabFromIndex = (tabIndex) => TABLE_TYPES_KEYS[tabIndex];

export const getIndexOfTableTab = (tabName) =>
  TABLE_TYPES_ENTRIES.findIndex(([key]) => key === tabName);

export const isSelectedTableTabPending = (selectedTableTab) =>
  selectedTableTab === TABLE_TYPES_KEYS[0];

export const isSelectedTableTabHistorical = (selectedTableTab) =>
  selectedTableTab === TABLE_TYPES_KEYS[1];

export const generateTableExportFilename = (actionType, tableType) => {
  const actionTypeVal = SIDEBAR_ITEMS[actionType]?.replace(/[^a-zA-Z0-9]/g, "");

  // generate filename
  switch (tableType) {
    case "pending":
      return `${actionTypeVal}_Pending_${dayjs().format(
        DATE_FORMAT_EXPORT_FILE
      )}.csv`;

    case "historical":
      return `${actionTypeVal}_Historical_${dayjs().format(
        DATE_FORMAT_EXPORT_FILE
      )}.csv`;

    default:
      return `${actionTypeVal}_${dayjs().format(DATE_FORMAT_EXPORT_FILE)}.csv`;
  }
};

export const downloadTableExportFile = (filename, response) => {
  // create a blob and a URL for the file
  const blob = new Blob([response.data], {
    type: response.headers["content-type"],
  });
  const url = window.URL.createObjectURL(blob);

  // create a temporary link element
  const link = document.createElement("a");
  link.href = url;
  // set the filename
  link.download = filename;
  // append the link to the DOM
  document.body.appendChild(link);

  // trigger the download
  link.click();

  // remove the link after download
  link.remove();

  // release the object URL
  window.URL.revokeObjectURL(url);
};

export const formatDateString = (dateStr, isStartOfDay = true) => {
  // parse the date in UTC and set the correct time based on isStartOfDay flag
  const adjustedDate = isStartOfDay
    ? dayjs.utc(dateStr).startOf("day") // 00:00:00.000Z
    : dayjs.utc(dateStr).endOf("day"); // 23:59:59.999Z

  // format the date in UTC with the required format
  return adjustedDate.format("YYYY-MM-DDTHH:mm:ss.SSS[Z]");
};

export const getProductCatalogueStatus = (status) => {
  if (status === "PENDING_APPROVAL") return "Pending Approval";
  if (status === "APPROVED") return "Approved";
  if (status === "REJECTED") return "Rejected";
  return "";
};

export const actionsHasType = (actionTypes, desiredType) =>
  actionTypes?.some((action) => action === desiredType);

export const isCurrencyNotAvailable = (currency) =>
  !currency ||
  currency === null ||
  currency?.trim().length === 0 ||
  !isNaN(parseInt(currency));

export const extractProductCatalogFailureReasonList = (data) => {
  return flatMap(data, (entries, ratePlanChargeId) =>
    flatMap(entries, (entry) =>
      entry.success
        ? []
        : entry.reasons.map((reason) => ({
            id: uuidv4(),
            ratePlanChargeId,
            failureReason: reason.message,
          }))
    )
  );
};
